import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ChevronLeft, Film, Newspaper, Star, Archive } from "lucide-react";
import { MalayalamLayout } from "../components/malayalam/MalayalamLayout";
import { useMalayalamLang } from "../context/MalayalamLangContext";
import { C, IMG } from "../components/malayalam/mlShared";

const FeatureCard = ({ icon: Icon, color, title, desc, img, to, testid, t }) => (
  <Link to={to} data-testid={testid}
    className="group flex flex-col rounded-xl border p-3 transition-all hover:-translate-y-1 hover:shadow-lg"
    style={{ background: C.card, borderColor: C.line }}>
    <div className="mb-3 flex items-center gap-2.5">
      <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: color }}>
        <Icon className="text-white" style={{ width: 18, height: 18 }} />
      </span>
      <h3 className="font-display text-lg font-semibold tracking-wide" style={{ color: C.navy }}>{t(title)}</h3>
    </div>
    <div className="overflow-hidden rounded-lg border" style={{ borderColor: C.line }}>
      <img src={img} alt={title} className="h-28 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
    </div>
    <div className="mt-3 flex items-center justify-between">
      <p className="pr-2 text-[13px] leading-snug" style={{ color: C.muted }}>{t(desc)}</p>
      <ChevronRight className="h-5 w-5 shrink-0" style={{ color: C.gold }} />
    </div>
  </Link>
);

const MiniCol = ({ title, color, thumbs, to, testid, t }) => (
  <div data-testid={testid}>
    <div className="mb-2.5 flex items-baseline justify-between">
      <h3 className="font-display text-xl font-semibold" style={{ color }}>{t(title)}</h3>
      <Link to={to} className="inline-flex items-center gap-0.5 text-xs font-semibold" style={{ color: C.gold }}>
        {t("View All")} <ChevronRight className="h-3.5 w-3.5" />
      </Link>
    </div>
    <div className="flex gap-2">
      {thumbs.map((th, i) => (
        <div key={i} className="overflow-hidden rounded-md border" style={{ borderColor: C.line, flex: 1 }}>
          <img src={th} alt="" className="h-24 w-full object-cover transition-transform duration-500 hover:scale-105" />
        </div>
      ))}
    </div>
  </div>
);

export default function MalayalamHome() {
  const { t } = useMalayalamLang();
  return (
    <MalayalamLayout>
      <section className="relative" data-testid="ml-hero">
        <div className="relative h-[440px] w-full overflow-hidden">
          <img src={IMG.hero} alt="Malayalam cinema film set" className="h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(251,246,236,0.97) 0%, rgba(251,246,236,0.82) 34%, rgba(251,246,236,0.15) 62%, rgba(251,246,236,0) 80%)" }} />
          <div className="absolute inset-0">
            <div className="mx-auto flex h-full max-w-[1500px] flex-col justify-center px-6">
              <h1 className="font-display font-bold leading-[1.02]" style={{ color: C.navy }} data-testid="ml-hero-title">
                <span className="block text-5xl lg:text-6xl">{t("Celebrating")}</span>
                <span className="block text-5xl lg:text-6xl">{t("Malayalam Cinema")}</span>
              </h1>
              <p className="mt-4 text-lg font-medium" style={{ color: C.ink }}>
                {t("Movies")} <span style={{ color: C.gold }}>•</span> {t("Music")} <span style={{ color: C.gold }}>•</span> {t("People")} <span style={{ color: C.gold }}>•</span> {t("History")} <span style={{ color: C.gold }}>•</span> {t("Television")} <span style={{ color: C.gold }}>•</span> {t("OTT")}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/malayalam/movies" data-testid="ml-explore-movies"
                  className="inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white transition-transform hover:scale-[1.03]"
                  style={{ background: C.rust }}>
                  {t("Explore Movies")} <ChevronRight className="h-4 w-4" />
                </Link>
                <Link to="/malayalam/cinema-history" data-testid="ml-discover-archive"
                  className="inline-flex items-center gap-2 rounded-md border-2 bg-white/40 px-6 py-3 text-sm font-semibold uppercase tracking-wide transition-colors hover:bg-white/70"
                  style={{ borderColor: C.navy, color: C.navy }}>
                  {t("Discover the Archive")} <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 py-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <FeatureCard t={t} testid="ml-card-now-showing" to="/malayalam/movies" icon={Film} color={C.rust} title="NOW SHOWING" img={IMG.nowShowing} desc="What's in theatres now across Kerala" />
          <FeatureCard t={t} testid="ml-card-latest-news" to="/malayalam/news" icon={Newspaper} color={C.teal} title="LATEST NEWS" img={IMG.news} desc="Latest updates from the world of Malayalam cinema" />
          <FeatureCard t={t} testid="ml-card-meet-star" to="/malayalam/meet-the-star" icon={Star} color={C.gold} title="MEET THE STAR" img={IMG.star} desc="Profiles, journeys and stories of cinema personalities" />
          <FeatureCard t={t} testid="ml-card-archive" to="/malayalam/memories" icon={Archive} color={C.olive} title="FROM THE ARCHIVE" img={IMG.archive} desc="Rare photos, articles and timeless treasures" />
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 pb-14">
        <div className="relative flex items-center gap-3">
          <button className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full border md:flex" style={{ borderColor: C.line, color: C.navy }} aria-label="Previous" data-testid="ml-carousel-prev">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="grid flex-1 grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <MiniCol t={t} testid="ml-col-upcoming" to="/malayalam/movies" title="Upcoming Movies" color={C.rust} thumbs={[IMG.kerala1, IMG.kerala2, IMG.kerala3]} />
            <MiniCol t={t} testid="ml-col-interviews" to="/malayalam/interviews" title="Interviews" color={C.teal} thumbs={[IMG.intv1, IMG.intv2]} />
            <MiniCol t={t} testid="ml-col-history" to="/malayalam/cinema-history" title="Cinema History" color={C.gold} thumbs={[IMG.hist1, IMG.hist2]} />
            <MiniCol t={t} testid="ml-col-memories" to="/malayalam/memories" title="Memories" color={C.olive} thumbs={[IMG.mem1, IMG.mem2]} />
          </div>
          <button className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full border md:flex" style={{ borderColor: C.line, color: C.navy }} aria-label="Next" data-testid="ml-carousel-next">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </section>
    </MalayalamLayout>
  );
}
